import { BaseAPI } from 'API/BaseApi';
import { ICheckDepositResponse } from '../../../types/order/escrow/ICheckDeposit';
import {
  convertIEscrowFileCompleteResponseDataToIEscrowFileData,
  IEscrowFileCompleteResponse,
} from '../../../types/order/escrow/response/IEscrowFileCompleteResponse';
import { IEscrowFileCompleteData } from '../../../types/order/escrow/IEscrowFileCompleteData';
import { eAPITags } from 'API/eAPITags';
import {
  IACHDepositRequest,
  ICheckDepositRequest,
  IDepositID,
  IWireDepositRequest,
} from '../../../types/order/escrow/types/IncomingEscrowTypes';
import { IWireDepositResponse } from '../../../types/order/escrow/response/IWireDepositResponse';
import { IAchPullDepositResponse } from '../../../types/order/escrow/response/IAchPullDepositResponse';
import { IEscrowBalancesResponse } from '../../../types/order/escrow/response/IEscrowBalancesResponse';
import { convertResponseToData } from './getEscrowBalancesForOrderId';
import { IEscrowBalancesData } from '../../../types/order/escrow/data/IEscrowBalancesData';
import { IWireRoutingResponse } from 'types/order/escrow/IWireRoutingResponse';
import { IDisburseAllResult } from 'types/order/escrow/IDisburseAllResult';
import { IDisburseAllRequest } from 'types/order/escrow/IDisburseAllRequest';

// This transforms '' to undefined
// This does not handle arrays at this time
export const emptyFieldsToUndefined = (obj: object) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === '') {
      obj[key] = undefined;
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      emptyFieldsToUndefined(obj[key]);
    }
  });
  return obj;
};

export const EscrowBaseAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    // This get fetches all incoming funds - check deposits, wire, and ACH
    getDetailedEscrowData: build.query<IEscrowFileCompleteData, string>({
      query: (orderId) => `/timber/api/internal/escrow/files/order/${orderId}`,
      transformResponse: (baseReturnValue: IEscrowFileCompleteResponse) =>
        convertIEscrowFileCompleteResponseDataToIEscrowFileData(baseReturnValue),
      providesTags: (result, error, orderId) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
      ],
    }),
    getBankNameByRoutingNumber: build.query<IWireRoutingResponse, string>({
      query: (routingNumber) => `/escrow/v1/wire_routing/${routingNumber}`,
    }),

    //All Incoming Checks Endpoints
    createCheckDeposit: build.mutation<ICheckDepositResponse, Omit<ICheckDepositRequest, 'id'>>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/${request.escrowFileId}/expected-incoming-checks`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
      ],
    }),
    updateCheckDeposit: build.mutation<ICheckDepositResponse, ICheckDepositRequest>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/expected-incoming-checks/${request.id}`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
      ],
    }),
    deleteCheckDeposit: build.mutation<void, IDepositID>({
      query: ({ id }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-checks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingCheckExpected, id: 'LIST' },
      ],
    }),

    //All Incoming Wires Endpoints
    createWireDeposit: build.mutation<IWireDepositResponse, Omit<IWireDepositRequest, 'id'>>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/${request.escrowFileId}/expected-incoming-wires`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
      ],
    }),
    updateWireDeposit: build.mutation<IWireDepositResponse, IWireDepositRequest>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/expected-incoming-wires/${request.id}`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
      ],
    }),
    deleteWireDeposit: build.mutation<void, IDepositID>({
      query: ({ id }) => ({
        url: `/timber/api/internal/escrow/expected-incoming-wires/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingWireExpected, id: 'LIST' },
      ],
    }),

    //All Incoming ACH Endpoints
    createACHDeposit: build.mutation<IAchPullDepositResponse, Omit<IACHDepositRequest, 'id'>>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/${request.escrowFileId}/expected-ach-pull-deposits`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
      ],
    }),
    updateACHDeposit: build.mutation<IAchPullDepositResponse, IACHDepositRequest>({
      query: (request) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits/${request.id}`,
        method: 'PUT',
        body: request,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
      ],
    }),
    deleteACHDeposit: build.mutation<IAchPullDepositResponse, IDepositID>({
      query: ({ id }) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.IncomingACHExpected, id: 'LIST' },
      ],
    }),
    sendACHDeposit: build.mutation<IAchPullDepositResponse, IDepositID>({
      query: ({ id }) => ({
        url: `/timber/api/internal/escrow/expected-ach-pull-deposits/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: eAPITags.DetailedEscrowData, id: orderId }],
    }),

    // This get fetches all escrow balances for order id data on fees 2.0
    getEscrowBalancesData: build.query<IEscrowBalancesData, string>({
      query: (orderId) => `/escrow/v1/balances/${orderId}`,
      transformResponse: (baseReturnValue: IEscrowBalancesResponse) => convertResponseToData(baseReturnValue),
      providesTags: (result, error, orderId) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
    markFundingCompleted: build.mutation<void, { orderId: string }>({
      query: ({ orderId }) => ({ url: `/timber/api/internal/orders/${orderId}/funding-complete`, method: 'POST' }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.FpeStatuses, id: orderId },
      ],
    }),
    disburseAll: build.mutation<IDisburseAllResult, IDisburseAllRequest>({
      query: ({ escrowId, orderId, ...body }) => ({
        url: `/timber/api/internal/escrow/${escrowId}/disbursements/send`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { orderId, wireIds, manualCheckIds, achIds, internalTransferIds }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.FpeStatuses, id: orderId },
        ...achIds.map((t) => ({ type: eAPITags.ACHDisbursement, id: t })),
        ...wireIds.map((t) => ({ type: eAPITags.WireDisbursement, id: t })),
        ...manualCheckIds.map((t) => ({ type: eAPITags.CheckDisbursement, id: t })),
        ...internalTransferIds.map((t) => ({ type: eAPITags.InternalTransferDisbursement, id: t })),
      ],
    }),
  }),
});
export const {
  useGetDetailedEscrowDataQuery,
  useGetBankNameByRoutingNumberQuery,
  useCreateCheckDepositMutation,
  useUpdateCheckDepositMutation,
  useDeleteCheckDepositMutation,
  useCreateWireDepositMutation,
  useUpdateWireDepositMutation,
  useDeleteWireDepositMutation,
  useCreateACHDepositMutation,
  useUpdateACHDepositMutation,
  useDeleteACHDepositMutation,
  useSendACHDepositMutation,
  useGetEscrowBalancesDataQuery,
  useMarkFundingCompletedMutation,
  useDisburseAllMutation,
} = EscrowBaseAPI;
